import GetRequest from "csi/comm/GetRequest";
import Cookie from "js-cookie";
import * as api from "../api";

export const SLACKBOT_USER_ID = "USLACKBOT";

export interface SlackApiResponse {

	ok: boolean;
	warning?: string;
	error?: string;
	needed?: string;
	provided?: string;
}

export interface ConversationHistory extends SlackApiResponse {
	messages: Message[];
	responseMetadata?: ResponseMetadata;
}

export interface ConversationList extends SlackApiResponse {
	channels: Conversation[];
	responseMetadata?: ResponseMetadata;
}

export interface UsersList extends SlackApiResponse {
	offset: string; // user id
	members: User[];
	cacheTs: string;
	responseMetadata?: ResponseMetadata;
}

export interface UserConversations extends SlackApiResponse {
	channels: Conversation[];
	responseMetadata?: ResponseMetadata;
}

export interface ConversationsCreate extends SlackApiResponse {

	// {
	//   "ok": false,
	//   "error": "invalid_name_maxlength",
	//   "detail": "Value passed for `name` exceeded max length."
	// }
	detail?: string;

	channel: Conversation;
}

export interface ConversationsJoin extends SlackApiResponse {
	channel: Conversation;

	responseMetadata?: WarningResponseMetadata;
}

export interface Conversation extends api.Channel {
	enterpriseId: string;
	id: string;
	name: string;
	created: string;
	creator: string;
	unlinked: number;
	nameNormalized: string;
	pendingShared: string[];
	lastRead: string;
	topic: Topic;
	purpose: Purpose;
	previousNames: string[];
	numOfMembers: number;
	//latest: Latest;
	locale: string;
	unreadCount: number;
	unreadCountDisplay: number;
	user: string; // conversations.open
	priority: number;

	dateConnected: number;

	sharedTeamIds: string[];

	parentConversation: string;
	pendingConnectedTeamIds: string[];

	// shared channels
	conversationHostId: string;
	internalTeamIds: string[];
	connectedTeamIds: string[];

	channel: boolean;
	group: boolean;
	im: boolean;
	archived: boolean;
	general: boolean;
	readOnly: boolean;
	threadOnly: boolean;
	nonThreadable: boolean;
	shared: boolean;
	extShared: boolean;
	orgShared: boolean;
	pendingExtShared: boolean;
	globalShared: boolean;
	orgDefault: boolean;
	orgMandatory: boolean;
	moved: number;
	member: boolean;
	open: boolean;
	private: boolean;
	mpim: boolean;
}

export interface Topic {
	value: string;
	creator: string;
	lastSet: number;
}

export interface Purpose {
	value: string;
	creator: string;
	lastSet: number;
}

export interface User extends api.User {
	teamId: string;
	name: string;
	deleted: boolean;
	color: string;
	realName: string;
	tz: string;
	tzLabel: string;
	tzOffset: number;
	profile: UserProfile;
	admin: boolean;
	owner: boolean;
	primaryOwner: boolean;
	invited_user: boolean;
	restricted: boolean;
	ultraRestricted: boolean;
	bot: boolean;
	stranger: boolean;
	appUser: boolean;
	updated: number;
	has2fa: boolean;
	presence: string;
	twoFactorType?: string;
	hasFiles?: boolean;
	locale: string;
	isWorkflowBot: boolean;
}

export interface UserProfile {
	guestChannels: string;
	guestInvitedBy: string;
	guestExpirationTs: number;
	avatarHash: string;
	statusText: string;
	statusTextCanonical: string;
	statusEmoji: string;
	statusExpiration: number;

	displayName: string;
	displayNameNormalized: string;
	realName: string;
	realNameNormalized: string;
	botId: string;

	title: string;
	email: string;
	skype: string;
	phone: string;
	team: string;

	apiAppId: string;
	alwaysActive: boolean;

	imageOriginal: string;

	image24: string;
	image32: string;
	image48: string;
	image72: string;
	image192: string;
	image512: string;
	image1024: string;
	customImage: boolean;
}

export interface ResponseMetadata {
	next_cursor: string;

	// e.g., [ERROR] must be less than 100 [json-pointer:/limit]
	messages?: string[];

	// e.g., method_deprecated
	warnings?: string[];
}

export interface WarningResponseMetadata {
	// e.g., method_deprecated
	warnings?: string[];
}

export interface Message {
	type: string;
	subtype?: string;
	botId?: string;
	user?: string;
	text?: string;
	blocks?: LayoutBlock[];
	attachments?: Attachment[];
	ts: string;
	files?: File[];
	reactions?: Reaction[];
	username?: string;
}

export interface Reaction {
	name: string;
	count: number;
	users: string[];
	url: string;
}

export interface Attachment {
	blocks?: LayoutBlock[];
	color?: string;
}

export const ATTACHMENT_LEGACY_FIELDS = [
	"author_icon",
	"author_link",
	"author_name",
	"fields",
	"footer",
	"footer_icon",
	"image_url",
	"mrkdwn_in",
	"pretext",
	"text",
	"thumb_url",
	"title",
	"title_link",
	"ts",
];

export interface File {
	id: string;
	name: string;
	thumbTiny: string;
	urlPrivate: string;
}

export interface BlockElement {
	type: string;
}

export interface ContextBlockElement {
	type: string;
}

export interface ImageElement extends BlockElement, ContextBlockElement {
	type: "image";
	imageUrl: string;
	altText: string;
}

export interface TextObject extends ContextBlockElement {
	type: "mrkdwn" | "plain_text";
}

export interface MarkdownTextObject extends TextObject {
	type: "mrkdwn";
	text: string;
	verbatim?: boolean;
}

export interface PlainTextObject extends TextObject {
	type: "plain_text";
	text: string;
	emoji?: boolean;
}

export interface LayoutBlock {
	type: string;
	blockId: string;
}

export interface ContextBlock extends LayoutBlock {
	type: "context";
	elements: BlockElement[];
}

export interface DividerBlock extends LayoutBlock {
	type: "divider";
}

export interface HeaderBlock extends LayoutBlock {
	type: "header";
	text: PlainTextObject;
}

export interface ImageBlock extends LayoutBlock {
	type: "image";
	imageUrl: string;
	altText: string;
	title?: PlainTextObject;
}

export interface SectionBlock extends LayoutBlock {
	text: TextObject;
	accessory?: BlockElement;
	fields?: TextObject[];
}

export interface RichTextBlock extends LayoutBlock {
	elements: BlockElement[];
}

export interface RichTextElement {
	type: string;
}

export interface RichTextSectionElement extends BlockElement, RichTextElement {
	elements: RichTextElement[];
}

export interface RichTextListElement extends BlockElement, RichTextElement {
	elements: RichTextElement[];
	style: string;  // bullet, ordered
	indent: number;
}

export interface RichTextQuoteElement extends BlockElement, RichTextElement {
	elements: RichTextElement[];
}

export interface RichTextPreformattedElement extends BlockElement, RichTextElement {
	elements: RichTextElement[];
}

export namespace RichTextSectionElement {
	export interface TextStyle {
		bold?: boolean;
		italic?: boolean;
		strike?: boolean;
		code?: boolean;
	}

	export interface Text extends RichTextElement {
		text: string;
		style?: TextStyle;
	}

	export interface Channel extends RichTextElement {
		channelId: string;
		style?: TextStyle;
	}

	export interface User extends RichTextElement {
		userId: string;
		style?: TextStyle;
	}

	export interface Emoji extends RichTextElement {
		name: string;
		skinTone: number;
		style?: TextStyle;
	}

	export interface Link extends RichTextElement {
		url: string;
		text: string;
		style?: TextStyle;
	}

	export interface Team extends RichTextElement {
		teamId: string;
		style?: TextStyle;
	}

	export interface UserGroup extends RichTextElement {
		usergroupId: string;
	}

	export interface Date extends RichTextElement {
		timestamp: string;
	}

	export interface Broadcast extends RichTextElement {
		range: string;  // channel, here, ..
	}

	export interface Color extends RichTextElement {
		value: string;
	}
}

export class SlackApiError extends Error {
	constructor(public apiResponse?: SlackApiResponse) {
		super(apiResponse.error);
	}
}

/**
 * Convert a timestamp returned by Slack API into 'Date' object
 *
 * @param ts
 */
export function ts2Date(ts: string): Date {
	return new Date(parseFloat(ts) * 1000);
}

export interface MessageGroup {
	date: Date;
	dateAsString: string;
	messages: Message[];
}

/**
 * Split messages into groups by date.
 * It keeps the order of messages
 *
 * @param messages
 */
export function groupMessagesByDate(messages: Message[]): MessageGroup[] {
	let currentGroup: MessageGroup = null;
	const groups: MessageGroup[] = [];
	messages.forEach((message) => {
		const date = ts2Date(message.ts);
		const dateAsString = date.toLocaleDateString();
		if (currentGroup == null || currentGroup.dateAsString != dateAsString) {
			currentGroup = {
				date,
				dateAsString,
				messages: [],
			};
			groups.push(currentGroup);
		}
		currentGroup.messages.push(message);
	});
	return groups;
}

/**
 * Returns the name of user used for UI
 */
export function getDisplayableUserName(userProfile: UserProfile): string {
	return userProfile?.displayName || userProfile?.realName;
}

export async function getListOfChannels(): Promise<ConversationList> {
	return slackOperationGet("GetListOfChannels");
}

export async function getUsers(channelId: string = ""): Promise<UsersList> {
	const formData = new FormData();
	formData.append("Channel", channelId);

	return slackOperationPost("GetUsers", formData);
}

export async function getUserConversations(): Promise<UserConversations> {
	return slackOperationGet("GetUserConversations");
}

export async function getChannelMessages(channelId: string): Promise<ConversationHistory> {
	return slackOperationGet("GetChannelMessages", { Channel: channelId });
}

type UserMessageType = "text" | "draftjs";

export async function postMessageToChannel(channelId: string, userMessage: string, userMessageType: UserMessageType, urlToUnfurl: string): Promise<SlackApiResponse> {
	const formData = new FormData();
	formData.append("Channel", channelId);
	formData.append("Message", userMessage);
	formData.append("MessageType", userMessageType);
	formData.append("UrlToUnfurl", urlToUnfurl);

	return slackOperationPost("PostMessageToChannel", formData);
}

export async function createChannel(channelName: string, isPrivate: boolean): Promise<ConversationsCreate> {
	const formData = new FormData();
	formData.append("Name", channelName);
	formData.append("IsPrivate", String(isPrivate));

	return slackOperationPost("CreateChannel", formData);
}

export async function inviteUsersToChannel(channelId: string, userIds: string[]): Promise<SlackApiResponse> {
	const formData = new FormData();
	formData.append("Channel", channelId);
	userIds.forEach((userId) => formData.append("Users", userId));

	return slackOperationPost("InviteUsers", formData);
}

export async function setChannelPurpose(channelId: string, purpose: string): Promise<SlackApiResponse> {
	const formData = new FormData();
	formData.append("Channel", channelId);
	formData.append("Purpose", purpose);

	return slackOperationPost("SetChannelPurpose", formData);
}

export async function joinChannel(channelId: string): Promise<ConversationsJoin> {
	const formData = new FormData();
	formData.append("Channel", channelId);

	return slackOperationPost("JoinChannel", formData);
}

//----------------------------------------------------------------------------------------------------
//                   Utility functions
//----------------------------------------------------------------------------------------------------

async function slackOperationGet<T extends SlackApiResponse>(operationName: string, parameters?: Record<string, string>): Promise<T> {
	const getRequest = new GetRequest("Slack", operationName);
	if (parameters) {
		for (const [key, value] of Object.entries(parameters)) {
			getRequest.addParameter(key, value);
		}
	}

	const response = await fetch(getRequest.toRequest());
	const json = await response.json();
	checkResponse(json);
	return json;
}

async function slackOperationPost<T extends SlackApiResponse>(operationName: string, formData: FormData): Promise<T> {
	const requestUrl = new GetRequest("Slack", operationName)
		.toRequest();

	//TODO: handle errors here using 'try/catch'
	const response = await fetch(requestUrl, {
		method: "POST",
		body: formData,
		headers: {
			"X-CSRF-Token": Cookie.get("CSRFToken"),
		},
	});

	const json = await response.json();
	checkResponse(json);
	return json;
}

function checkResponse(response: SlackApiResponse): void {
	if (!response.ok) {
		throw new SlackApiError(response);
	}
}